import { z } from "zod";

import { type Rol } from "./enums";

/**
 * Contratos de la activación de cuenta por invitación (ADR 053).
 *
 * Flujo: el SUPER_ADMIN crea la institución + su primer ADMIN (cuenta INACTIVA,
 * sin contraseña) y se emite un token de un solo uso. El invitado abre el enlace
 * `/activar/:token`, el front pide los datos públicos de la invitación (para
 * mostrarle a quién/qué institución pertenece) y define su contraseña. El token
 * NUNCA viaja en el body: va en la URL (path param) para no quedar en logs de
 * body ni en el historial de formularios.
 */

/**
 * Datos para invitar a un DOCENTE (ADR 055). Lo hace el ADMIN de la institución;
 * el `institucionId` se deriva del actor (no viaja en el body). El docente nace
 * INACTIVO y activa su cuenta con el mismo flujo `/activar/:token`. Las
 * capacidades (asesor/jurado, ADR 054) se asignan después, no al invitar.
 *
 * ADR 055 (enmienda) — `escuelaIds`: EAPs a las que se afilia el docente (puede
 * ser varias, opcional al invitar). Deben ser del mismo tenant. Reemplaza al
 * antiguo `departamentoId`.
 */
export const invitarDocenteSchema = z.object({
  nombres: z.string().trim().min(2).max(120),
  apellidos: z.string().trim().min(2).max(120),
  correo: z.string().trim().toLowerCase().email("Correo inválido"),
  documentoIdentidad: z.string().trim().min(8).max(15),
  escuelaIds: z.array(z.string().uuid()).optional(),
});
export type InvitarDocenteInput = z.infer<typeof invitarDocenteSchema>;

/**
 * Datos para invitar a un ESTUDIANTE (ADR 055). Igual que el docente, pero la
 * `escuelaId` es OBLIGATORIA (el PerfilEstudiante la requiere) y debe ser del
 * mismo tenant. El código de estudiante lo autogenera el backend.
 */
export const invitarEstudianteSchema = z.object({
  nombres: z.string().trim().min(2).max(120),
  apellidos: z.string().trim().min(2).max(120),
  correo: z.string().trim().toLowerCase().email("Correo inválido"),
  documentoIdentidad: z.string().trim().min(8).max(15),
  escuelaId: z.string().uuid(),
});
export type InvitarEstudianteInput = z.infer<typeof invitarEstudianteSchema>;

/** Define la contraseña al activar la cuenta invitada. */
export const activarCuentaSchema = z
  .object({
    password: z.string().min(8, "Mínimo 8 caracteres"),
    passwordConfirmacion: z.string().min(8, "Mínimo 8 caracteres"),
  })
  .refine((data) => data.password === data.passwordConfirmacion, {
    message: "Las contraseñas no coinciden",
    path: ["passwordConfirmacion"],
  });
export type ActivarCuentaInput = z.infer<typeof activarCuentaSchema>;

/**
 * Datos públicos de una invitación válida, para pintar la página de activación
 * (a quién pertenece + de qué institución). NO incluye nada sensible: ni el
 * token, ni hashes, ni el rol concreto. Si el token es inválido/expirado/usado,
 * el backend responde un error de dominio (no este wire).
 */
export interface InvitacionPublicaWire {
  correo: string;
  nombres: string;
  apellidos: string;
  /** Rol con el que se creó la cuenta invitada (ADR 053/055). El front pinta el
   * copy de activación según este rol (no asumir "administrador"). */
  rol: Rol;
  institucion: {
    slug: string;
    nombre: string;
    nombreCorto: string;
  };
}
